/**
 * Locale-aware formatting helpers.
 *
 * Backed entirely by the JavaScript `Intl` namespace (no extra deps).
 * All helpers accept a `Locale` ("fr" | "en") and gracefully fall back
 * to a sensible non-throwing string if the input is malformed.
 *
 * See spec §6 (i18n): dates and numbers are formatted using `Intl`.
 */

import { useTranslation } from 'react-i18next';

export type Locale = 'fr' | 'en';

/**
 * Coerces an arbitrary value (i18n `i18next.language` may be e.g. "fr-FR")
 * down to one of the two supported app locales.
 */
export function normalizeLocale(input: string | undefined | null): Locale {
  if (!input) return 'fr';
  // Match the two-letter prefix; everything else falls back to French.
  return input.startsWith('en') ? 'en' : 'fr';
}

/**
 * Returns a "medium date + short time" formatted string for the given ISO
 * timestamp. Falls back to the raw ISO string if parsing fails.
 */
export function formatDate(iso: string, locale: Locale): string {
  if (typeof iso !== 'string' || iso.length === 0) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return iso;
  }
}

/**
 * Returns a "short date only" formatted string for the given ISO timestamp.
 * Useful for tables where the full datetime would be too dense.
 */
export function formatDateShort(iso: string, locale: Locale): string {
  if (typeof iso !== 'string' || iso.length === 0) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(d);
  } catch {
    return iso;
  }
}

/**
 * Returns a relative-time string ("il y a 1 heure", "in 5 minutes"…)
 * computed from `now` (defaults to current wall clock). Picks an appropriate
 * unit (seconds → minutes → hours → days → months → years) based on the
 * magnitude of the diff.
 *
 * Returns "—" on malformed input.
 */
const RELATIVE_THRESHOLDS: ReadonlyArray<{
  limit: number;
  divisor: number;
  unit: Intl.RelativeTimeFormatUnit;
}> = [
  { limit: 60, divisor: 1, unit: 'second' },
  { limit: 3600, divisor: 60, unit: 'minute' },
  { limit: 86400, divisor: 3600, unit: 'hour' },
  { limit: 2_592_000, divisor: 86400, unit: 'day' },
  { limit: 31_536_000, divisor: 2_592_000, unit: 'month' },
  { limit: Number.POSITIVE_INFINITY, divisor: 31_536_000, unit: 'year' },
];

export function formatRelative(
  iso: string,
  locale: Locale,
  now: Date = new Date(),
): string {
  if (typeof iso !== 'string' || iso.length === 0) return '—';
  const past = Date.parse(iso);
  if (Number.isNaN(past)) return '—';
  const diffMs = past - now.getTime();
  const absSeconds = Math.round(Math.abs(diffMs) / 1000);
  const sign = diffMs < 0 ? -1 : 1;

  let value = 0;
  let unit: Intl.RelativeTimeFormatUnit = 'second';
  for (const t of RELATIVE_THRESHOLDS) {
    if (absSeconds < t.limit) {
      value = Math.round(absSeconds / t.divisor);
      unit = t.unit;
      break;
    }
  }

  try {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
      sign * value,
      unit,
    );
  } catch {
    return iso;
  }
}

/**
 * Returns a locale-formatted number string. Honors the locale's decimal
 * separator and thousand grouping (FR uses non-breaking narrow space
 * between thousands; EN uses comma).
 */
export function formatNumber(n: number, locale: Locale): string {
  if (!Number.isFinite(n)) return String(n);
  try {
    return new Intl.NumberFormat(locale).format(n);
  } catch {
    return String(n);
  }
}

/**
 * Returns a humanized byte-count string ("12,3 Mo" / "12.3 MB") using a
 * 1024-based scale up to TB. The numeric portion is locale-formatted.
 */
const BYTE_UNITS: Readonly<Record<Locale, ReadonlyArray<string>>> = {
  fr: ['o', 'Ko', 'Mo', 'Go', 'To'],
  en: ['B', 'KB', 'MB', 'GB', 'TB'],
};

export function formatBytes(n: number, locale: Locale): string {
  if (!Number.isFinite(n) || n < 0) return String(n);
  const units = BYTE_UNITS[locale];
  let value = n;
  let idx = 0;
  while (value >= 1024 && idx < units.length - 1) {
    value /= 1024;
    idx += 1;
  }
  try {
    const formatted = new Intl.NumberFormat(locale, {
      maximumFractionDigits: idx === 0 ? 0 : 1,
    }).format(value);
    return `${formatted} ${units[idx]}`;
  } catch {
    return `${value} ${units[idx]}`;
  }
}

/**
 * Hook returning the same formatters bound to the current i18n language.
 * Use in components instead of repeatedly threading `lang` through props.
 */
export interface LocaleFormatters {
  locale: Locale;
  formatDate: (iso: string) => string;
  formatDateShort: (iso: string) => string;
  formatRelative: (iso: string, now?: Date) => string;
  formatNumber: (n: number) => string;
  formatBytes: (n: number) => string;
}

export function useLocaleFormatters(): LocaleFormatters {
  const { i18n } = useTranslation();
  const locale = normalizeLocale(i18n.language);
  return {
    locale,
    formatDate: (iso) => formatDate(iso, locale),
    formatDateShort: (iso) => formatDateShort(iso, locale),
    formatRelative: (iso, now) => formatRelative(iso, locale, now),
    formatNumber: (n) => formatNumber(n, locale),
    formatBytes: (n) => formatBytes(n, locale),
  };
}
